<script>
  import { onMount } from 'svelte';

  import { Column, ClickableTile, InlineNotification, Row, SkeletonPlaceholder } from 'carbon-components-svelte';

  import BuildsConfigurator32 from 'carbon-icons-svelte/lib/Cube32';
  import Building32 from 'carbon-icons-svelte/lib/Building32';
  import Email32 from 'carbon-icons-svelte/lib/Email32';
  import DataSet32 from 'carbon-icons-svelte/lib/DataSet32';
  import Events32 from 'carbon-icons-svelte/lib/Events32';
  import License32 from 'carbon-icons-svelte/lib/License32';
  import Store32 from 'carbon-icons-svelte/lib/Store32';
  import Subscription32 from 'carbon-icons-svelte/lib/GuiManagement32';
  import Timer32 from 'carbon-icons-svelte/lib/Timer32';
  import UserMultiple32 from 'carbon-icons-svelte/lib/UserMultiple32';

  import { useNavigate } from 'svelte-navigator';

  import { getInternalPermissions } from '../services';

  const GRID_BREAKPOINTS = {
    sm: {
      span: 4,
      offset: 0,
    },
    md: {
      span: 4,
      offset: 0,
    },
    lg: {
      span: 4,
      offset: 0,
    },
    xlg: {
      span: 4,
      offset: 0,
    },
    max: {
      span: 4,
      offset: 0,
    },
  };

  const navigate = useNavigate();

  const MODULES = [
    { href: '/users', icon: UserMultiple32, title: 'Users' },
    { href: '/groups', icon: Events32, title: 'Groups' },
    { href: '/organizations', icon: Building32, title: 'Organizations' },
    { href: '/customer-builds', icon: BuildsConfigurator32, title: 'Builds' },
    { href: '/licenses', icon: License32, title: 'Licenses' },
    { href: '/workbench', icon: Subscription32, title: 'Workbench' },
    { href: '/data-feeds', icon: Store32, title: 'Inventory' },
    { href: '/tasks', icon: Timer32, title: 'Tasks' },
    { href: '/emails', icon: Email32, title: 'Emails' },
    { href: '/reports', icon: DataSet32, title: 'Reports' },
  ];

  let pageData = { loading: true };

  function handleTileClick(destination) {
    return function onClick(event) {
      event.preventDefault();
      navigate(destination);
    };
  }

  onMount(async () => {
    try {
      const permissions = await getInternalPermissions();
      pageData = { permissions, loading: false };
    } catch (error) {
      console.error('[Home] Failed to retrieve permissions!', error);
      pageData = { loading: false, error };
    }
  });
</script>

{#if pageData.error}
  <InlineNotification
    hideCloseButton
    kind="error"
    lowContrast
    title="Error:"
    subtitle="Failed to load your access permissions! Please try again later."
  />
{:else if !pageData.loading && (!pageData.permissions || pageData.permissions.length === 0)}
  <InlineNotification
    hideCloseButton
    kind="warning"
    lowContrast
    title="Warning:"
    subtitle="You are not currently authorized to access any modules."
  />
{:else}
  <div class="modules">
    <Row>
      {#each MODULES as module (module.href)}
        <Column {...GRID_BREAKPOINTS}>
          {#if pageData.loading}
            <SkeletonPlaceholder class="tile-skeleton" />
          {:else}
            <div class="tile-wrapper">
              <ClickableTile on:click={handleTileClick(module.href)} href={module.href}>
                <h2 class="card-title">{module.title}</h2>
                <div class="card-action">
                  <svelte:component this={module.icon} />
                </div>
              </ClickableTile>
            </div>
          {/if}
        </Column>
      {/each}
    </Row>
  </div>
{/if}

<style>
  .card-title {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-variant: all-small-caps;
    border-left: 3px solid var(--cds-border-strong);
    padding-left: 0.5rem;
    font-size: 1rem;
    transition: all 0.15s cubic-bezier(0.2, 0, 0.38, 0.9);
  }

  .tile-wrapper:hover .card-title {
    border-color: var(--cds-focus);
  }

  .card-action {
    display: flex;
    justify-content: flex-end;
    padding-top: 1rem;
  }

  .card-action :global(svg) {
    fill: var(--cds-border-strong);
    transition: all 0.15s cubic-bezier(0.2, 0, 0.38, 0.9);
  }

  .tile-wrapper:hover .card-action :global(svg) {
    fill: var(--cds-focus);
  }

  .modules :global(.tile-skeleton) {
    width: 100%;
    height: 100px;
  }
</style>
